import { useEffect, useRef } from "react";
import { useWorkerInterval } from "./useWorkerInterval";

/** Slow enough to read in the tab strip, fast enough to read as "urgent". */
const FLASH_PERIOD_MS = 1200;

/**
 * Alternate the browser tab title while `active` — D5.
 *
 * This is the fallback path for the one failure the feature cannot tolerate:
 * the browser refusing `play()` because the page has never been interacted
 * with, so a call arrives and nothing at all happens. A tab that cannot make a
 * sound can still change its own label in the tab strip.
 *
 * Ticked from the Web Worker for the same reason the invite poll is (see
 * `../lib/tickerWorker.ts`): a blocked ringtone is *most* likely in a tab the
 * user left alone, which is exactly the tab whose main-thread timers Chrome
 * clamps to roughly one tick a minute. A main-thread `setInterval` here would
 * produce a title that changes once, then sits still for the rest of the ring.
 *
 * The title is captured when flashing starts and restored when it stops.
 * Nothing else in `frontend-shell` writes `document.title`, so there is no other
 * writer to race with.
 */
export function useTitleFlash(active: boolean, message: string): void {
  const originalRef = useRef<string | null>(null);
  const showingMessageRef = useRef(false);

  useEffect(() => {
    if (!active) return;

    originalRef.current = document.title;
    showingMessageRef.current = true;
    document.title = message;

    return () => {
      showingMessageRef.current = false;
      if (originalRef.current !== null) document.title = originalRef.current;
      originalRef.current = null;
    };
  }, [active, message]);

  useWorkerInterval(
    () => {
      showingMessageRef.current = !showingMessageRef.current;
      document.title = showingMessageRef.current ? message : (originalRef.current ?? document.title);
    },
    FLASH_PERIOD_MS,
    active,
  );
}
